import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Navbar from "./Navbar";
import "./Profile.css";
import { useNavigate } from "react-router-dom";
import UserProfileImage from "./UserProfileImage";
import Streak from "./Streak";
import Comments from "./Comments";
import LikeButton from "./LikeButton";
import useSmartLinks from "../hooks/useSmartLinks";
import ListLoader, { useProgressiveList } from "./ProgressiveList";

const BASE_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

const COLORS = ["#8b5cf6","#06b6d4","#ec4899","#f59e0b","#10b981","#3b82f6","#ef4444"];

const makeAvatar = (name = "?") => {
  const initials = name.trim().split(/\s+/).map(w => w[0] || "").join("").toUpperCase().slice(0,2) || "?";
  const color    = COLORS[name.charCodeAt(0) % COLORS.length];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
    <circle cx="50" cy="50" r="50" fill="${color}"/>
    <text x="50" y="50" dominant-baseline="central" text-anchor="middle"
      fill="white" font-size="38" font-family="sans-serif" font-weight="700">${initials}</text>
  </svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

function Avatar({ src, name = "?", size = 36 }) {
  const [err, setErr] = useState(false);
  useEffect(() => { setErr(false); }, [src]);
  const fallback = makeAvatar(name);
  return (
    <img
      src={!src || err ? fallback : src}
      alt={name}
      onError={() => setErr(true)}
      style={{
        width: size, height: size,
        borderRadius: "50%",
        objectFit: "cover",
        objectPosition: "center",
        display: "block",
        flexShrink: 0,
        border: "2px solid #080b14",
        background: "#1a1040",
      }}
    />
  );
}

function Profile() {
  const smartLinks = useSmartLinks("profile");
  const navigate  = useNavigate();
  const canvasRef = useRef(null);
  const videoRefs = useRef({});
  const menuRefs  = useRef({});

  const [userBlogs, setUserBlogs]         = useState([]);
  const [totalBlogs, setTotalBlogs]       = useState(0);
  const [username, setUsername]           = useState("");
  const [myProfilePic, setMyProfilePic]   = useState("");
  const [expandedBlogs, setExpandedBlogs] = useState({});
  const [followers, setFollowers]         = useState([]);
  const [following, setFollowing]         = useState([]);
  const [showDropdown, setShowDropdown]   = useState(null);
  const [openCommentId, setOpenCommentId] = useState(null);
  const [copied, setCopied]               = useState(false);
  const [blogsLoading, setBlogsLoading]   = useState(true);
  const [blogsError, setBlogsError]       = useState("");
  const [shareToast, setShareToast]       = useState(null);

  // ── LOGO MENU ── the app logo opens a vertical, downward menu holding the
  // Pro, Pro Access, Treat and Streak options. Treats sit under an arrow.
  const [logoMenuOpen, setLogoMenuOpen]   = useState(false);
  const [treatsOpen, setTreatsOpen]       = useState(false);
  // The menu's Streak slot; the (single) Streak card renders its buttons here.
  const [streakSlot, setStreakSlot]       = useState(null);
  const logoMenuRef = useRef(null);
  const [viewCounts, setViewCounts]       = useState({});
  const [commentCounts, setCommentCounts] = useState({});
  const [shareCounts, setShareCounts]     = useState({});
  const [saveCounts, setSaveCounts]       = useState({});

  // ── PIN FEATURE ──
  const [pinnedIds, setPinnedIds]         = useState([]);
  const [openMenuId, setOpenMenuId]       = useState(null);

  const token      = localStorage.getItem("token");
  // Batch size for progressive loading (was the per-page size).
  const blogsPerPage = 5;

  // ── PARTICLE CANVAS ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;
    const particles = Array.from({ length: 60 }, () => ({
      x:  Math.random() * canvas.width,
      y:  Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      r:  Math.random() * 1.5 + 0.5,
    }));
    let animId;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx   = particles[i].x - particles[j].x;
          const dy   = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(139,92,246,${1 - dist / 100})`;
            ctx.lineWidth = 0.4;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
        ctx.beginPath();
        ctx.arc(particles[i].x, particles[i].y, particles[i].r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(167,139,250,0.6)";
        ctx.fill();
        particles[i].x += particles[i].vx;
        particles[i].y += particles[i].vy;
        if (particles[i].x < 0 || particles[i].x > canvas.width)  particles[i].vx *= -1;
        if (particles[i].y < 0 || particles[i].y > canvas.height) particles[i].vy *= -1;
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);

  // ── FETCH OWN PROFILE PIC ──
  const fetchMyPic = async (name) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/profile/username/${name}`);
      if (res.data.profilePic) setMyProfilePic(res.data.profilePic);
    } catch {}
  };

  // ── FOLLOW DATA ──
  const fetchFollowData = async (name, tok) => {
    try {
      const [f1, f2] = await Promise.all([
        axios.get(`${BASE_URL}/api/users/${name}/followers`, { headers: { Authorization: `Bearer ${tok}` } }),
        axios.get(`${BASE_URL}/api/users/${name}/following`, { headers: { Authorization: `Bearer ${tok}` } }),
      ]);
      setFollowers(f1.data.followers || []);
      setFollowing(f2.data.following || []);
    } catch (err) { console.error(err); }
  };

  // ── MY BLOGS ── shows a loading indicator while fetching and a retryable
  // error instead of the misleading "No blogs yet" when the request fails.
  const loadBlogs = async (author, tok) => {
    setBlogsLoading(true);
    setBlogsError("");
    try {
      const res = await axios.get(`${BASE_URL}/api/blogs/profile/${author}`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      const blogs = res.data.blogs || [];
      setUserBlogs(blogs);
      setTotalBlogs(blogs.length);
      // seed view counts from blog data
      const counts = {};
      blogs.forEach(b => { counts[b._id] = b.viewCount || 0; });
      setViewCounts(counts);

      // seed engagement counts from the same response
      const comments = {}, shares = {}, saves = {};
      blogs.forEach(b => {
        comments[b._id] = b.commentCount ?? 0;
        shares[b._id]   = b.shareCount   ?? 0;
        saves[b._id]    = b.saveCount    ?? 0;
      });
      setCommentCounts(comments);
      setShareCounts(shares);
      setSaveCounts(saves);
    } catch (err) {
      console.error(err);
      setBlogsError("Could not load your blogs. Please try again.");
    } finally {
      setBlogsLoading(false);
    }
  };

  const retryBlogs = () => {
    const author = localStorage.getItem("username");
    const tok    = localStorage.getItem("token");
    if (author && tok) loadBlogs(author, tok);
  };

  // ── MAIN LOAD ──
  useEffect(() => {
    const load = async () => {
      const author = localStorage.getItem("username");
      const tok    = localStorage.getItem("token");
      if (!author || !tok) { navigate("/"); return; }
      setUsername(author);
      fetchMyPic(author);
      await loadBlogs(author, tok);
      fetchFollowData(author, tok);

      // load pinned blog ids for this user
      try {
        const saved = localStorage.getItem(`pinnedBlogs_${author}`);
        if (saved) setPinnedIds(JSON.parse(saved));
      } catch {}
    };
    load();
  }, [navigate]);

  // ── CLOSE 3-DOT MENU ON OUTSIDE CLICK ──
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        openMenuId &&
        menuRefs.current[openMenuId] &&
        !menuRefs.current[openMenuId].contains(e.target)
      ) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuId]);

  // ── CLOSE LOGO MENU ON OUTSIDE CLICK / ESCAPE ──
  // Only listens while the menu is open. The Streak options are portalled
  // into the menu's DOM, so taps on them count as "inside".
  useEffect(() => {
    if (!logoMenuOpen) return undefined;
    const handlePointer = (e) => {
      if (logoMenuRef.current && !logoMenuRef.current.contains(e.target)) {
        setLogoMenuOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setLogoMenuOpen(false);
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("touchstart", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("touchstart", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [logoMenuOpen]);

  // Menu links reuse the existing routes; the menu closes as it navigates.
  const goFromLogoMenu = (path) => {
    setLogoMenuOpen(false);
    navigate(path);
  };

  // ── TOGGLE PIN ──
  const togglePin = (id) => {
    setPinnedIds(prev => {
      const updated = prev.includes(id)
        ? prev.filter(p => p !== id)
        : [id, ...prev];
      try {
        localStorage.setItem(`pinnedBlogs_${username}`, JSON.stringify(updated));
      } catch {}
      return updated;
    });
    setOpenMenuId(null);
  };

  const isPinned = (id) => pinnedIds.includes(id);

  // ── TOGGLE POST SETTINGS (hideComments / allowShare / allowSave) ──
  // key is one of "hideComments" | "allowShare" | "allowSave".
  // hideComments defaults false (off); allowShare/allowSave default true (on).
  const toggleBlogSetting = async (blogId, key) => {
    const blog = userBlogs.find(b => b._id === blogId);
    if (!blog) return;

    const currentValue = key === "hideComments"
      ? Boolean(blog.hideComments)
      : blog[key] !== false;
    const newValue = !currentValue;

    // optimistic update so the UI responds instantly
    setUserBlogs(prev => prev.map(b => (b._id === blogId ? { ...b, [key]: newValue } : b)));
    setOpenMenuId(null);

    try {
      await axios.patch(
        `${BASE_URL}/api/blogs/settings/${blogId}`,
        { [key]: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error("Failed to update setting:", err);
      // revert on failure
      setUserBlogs(prev => prev.map(b => (b._id === blogId ? { ...b, [key]: currentValue } : b)));
    }
  };

  // ── TOGGLE READ MORE ──
  const toggleReadMore = (id) =>
    setExpandedBlogs(prev => ({ ...prev, [id]: !prev[id] }));

  // ── DELETE BLOG ──
  const deleteBlog = async (id) => {
    if (!window.confirm("Delete this blog?")) return;
    try {
      const tok = localStorage.getItem("token");
      await axios.delete(`${BASE_URL}/api/blogs/delete/${id}`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      const updated = userBlogs.filter(b => b._id !== id);
      setUserBlogs(updated);
      setTotalBlogs(updated.length);

      // clean up pin state if the deleted blog was pinned
      if (pinnedIds.includes(id)) {
        const updatedPins = pinnedIds.filter(p => p !== id);
        setPinnedIds(updatedPins);
        try {
          localStorage.setItem(`pinnedBlogs_${username}`, JSON.stringify(updatedPins));
        } catch {}
      }
    } catch (err) { console.error(err); }
  };

  // ── TOGGLE COMMENTS ──
  const toggleComments = (blogId) => {
    setOpenCommentId(prev => (prev === blogId ? null : blogId));
  };

  // ── SHARE ──
  const recordShare = async (blogId) => {
    setShareCounts(prev => ({ ...prev, [blogId]: (prev[blogId] ?? 0) + 1 }));
    try {
      const res = await axios.post(`${BASE_URL}/api/blogs/${blogId}/share`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (typeof res.data?.shareCount === "number") {
        setShareCounts(prev => ({ ...prev, [blogId]: res.data.shareCount }));
      }
    } catch (err) {
      console.error("Share count failed:", err);
      setShareCounts(prev => ({ ...prev, [blogId]: Math.max((prev[blogId] ?? 1) - 1, 0) }));
    }
  };

  const handleShare = async (blog) => {
    const shareUrl = `${window.location.origin}/blog/${blog._id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: blog.title, text: blog.title, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setShareToast(blog._id);
        setTimeout(() => setShareToast(null), 2000);
      }
    } catch (err) {
      // Share sheet dismissed or clipboard blocked — nothing was shared.
      console.error(err);
      return;
    }
    recordShare(blog._id);
  };

  // ── COPY PROFILE URL ──
  const copyProfileUrl = async () => {
    try {
      const safeUsername = encodeURIComponent(username.trim());
      const profileUrl   = `${window.location.origin}/authorprofile/${safeUsername}`;
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) { console.error("Copy failed:", err); }
  };

  // ── DISPLAY TITLE ──
  const getDisplayTitle = (name) => {
    if (!name) return "My Profile";
    const trimmed    = name.trim();
    const possessive = trimmed.endsWith("s") || trimmed.endsWith("S")
      ? `${trimmed}'`
      : `${trimmed}'s`;
    return `${possessive} Profile`;
  };

  // ── SORT: PINNED FIRST, THEN LOAD PROGRESSIVELY ──
  const sortedBlogs = [...userBlogs].sort((a, b) => {
    const aPinned = isPinned(a._id);
    const bPinned = isPinned(b._id);
    if (aPinned && !bPinned) return -1;
    if (!aPinned && bPinned) return 1;
    return 0; // stable sort keeps original order within each group
  });

  // No reset key: pinning, deleting or toggling a post setting keeps the
  // posts already loaded on screen, as the old page number used to stay put.
  const blogList     = useProgressiveList(sortedBlogs, blogsPerPage);
  const currentBlogs = blogList.visibleItems;

  return (
    <div className="profile-wrapper">
      <canvas ref={canvasRef} className="profile-canvas" />
      <Navbar />

      <div className="profile-page">

        {/* ── HEADER CARD ── */}
        <div className="profile-header-card">
          <div className="profile-avatar-wrap">
            <UserProfileImage
              onImageClick={() => navigate("/upload-status")}
            />
          </div>

          <div className="profile-badge">
            <span className="badge-dot" />
            Blogger
          </div>

          <div className="profile-name-wrap">
            <h1 className="profile-display-title">{getDisplayTitle(username)}</h1>
            <div className="profile-handle-row">
              <span className="profile-handle-at">@</span>
              <span className="profile-handle-name">
                {username.toLowerCase().replace(/\s/g, "_")}
              </span>
            </div>
            <div className="profile-url-box">
              <input
                type="text"
                readOnly
                value={`${window.location.origin}/authorprofile/${username}`}
                className="profile-url-input"
              />
              <button
                className={`profile-url-btn ${copied ? "copied-btn" : ""}`}
                onClick={copyProfileUrl}
              >
                {copied ? "Copied ✅" : "Copy URL"}
              </button>
            </div>
          </div>

          {/* STATS */}
          <div className="profile-stats-row">
            <div className="stat-item">
              <span className="stat-number">{totalBlogs}</span>
              <span className="stat-label">Blogs</span>
            </div>
            <div className="stat-divider" />
            <div
              className="stat-item stat-clickable"
              onClick={() => setShowDropdown(showDropdown === "followers" ? null : "followers")}
            >
              <span className="stat-number">{followers.length}</span>
              <span className="stat-label">
                Followers {showDropdown === "followers" ? "▲" : "▼"}
              </span>
            </div>
            <div className="stat-divider" />
            <div
              className="stat-item stat-clickable"
              onClick={() => setShowDropdown(showDropdown === "following" ? null : "following")}
            >
              <span className="stat-number">{following.length}</span>
              <span className="stat-label">
                Following {showDropdown === "following" ? "▲" : "▼"}
              </span>
            </div>
          </div>

          {/* DROPDOWNS */}
          <div className="follow-dropdowns-row">
            {showDropdown === "followers" && (
              <div className="follow-inline-dropdown">
                <p className="follow-inline-title">👥 Followers</p>
                {followers.length === 0 ? (
                  <p className="follow-empty">No followers yet</p>
                ) : followers.map(u => (
                  <div
                    key={u._id}
                    className="follow-dropdown-item"
                    onClick={() => navigate(`/authorprofile/${u.name}`)}
                  >
                    <Avatar src={u.profilePic} name={u.name} size={32} />
                    <span>{u.name}</span>
                  </div>
                ))}
              </div>
            )}
            {showDropdown === "following" && (
              <div className="follow-inline-dropdown">
                <p className="follow-inline-title">👤 Following</p>
                {following.length === 0 ? (
                  <p className="follow-empty">Not following anyone yet</p>
                ) : following.map(u => (
                  <div
                    key={u._id}
                    className="follow-dropdown-item"
                    onClick={() => navigate(`/authorprofile/${u.name}`)}
                  >
                    <Avatar src={u.profilePic} name={u.name} size={32} />
                    <span>{u.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── APP-LOGO MENU ──
            Same idea as the Home page's logo button (logo ↔ ✕), but the
            options open in a vertical list BELOW the logo instead of a circle.
            Every option reuses its existing route; nothing new is routed. */}
        <div className="profile-logo-menu" ref={logoMenuRef}>
          <button
            type="button"
            className={`profile-logo-toggle ${logoMenuOpen ? "open" : ""}`}
            onClick={() => setLogoMenuOpen(prev => !prev)}
            aria-label={logoMenuOpen ? "Close profile menu" : "Open profile menu"}
            aria-expanded={logoMenuOpen}
            aria-controls="profile-logo-panel"
          >
            {logoMenuOpen ? (
              <span className="profile-logo-close" aria-hidden="true">✕</span>
            ) : (
              <img src="/blog.png" alt="" className="profile-logo-img" />
            )}
          </button>
          {!logoMenuOpen && (
            <span className="profile-logo-caption">Pro · Treats · Streak</span>
          )}

          {logoMenuOpen && (
            <div id="profile-logo-panel" className="profile-logo-panel">
              <button
                type="button"
                className="profile-logo-item profile-logo-item-pro"
                onClick={() => goFromLogoMenu("/theblogssphere-pro")}
              >
                <span className="profile-logo-item-main">💎 TheBlogsSphere Pro</span>
                <span className="profile-logo-item-sub">Monetize Your Content</span>
              </button>

              <button
                type="button"
                className="profile-logo-item"
                onClick={() => goFromLogoMenu("/pro-access")}
              >
                <span className="profile-logo-item-main">🔓 Pro Access</span>
                <span className="profile-logo-item-sub">Creators you have unlocked</span>
              </button>

              {/* Downward arrow — expands / collapses the treat options */}
              <button
                type="button"
                className={`profile-logo-arrow ${treatsOpen ? "open" : ""}`}
                onClick={() => setTreatsOpen(prev => !prev)}
                aria-expanded={treatsOpen}
                aria-controls="profile-logo-treats"
                aria-label={treatsOpen ? "Hide treat options" : "Show treat options"}
              >
                <span>🎁 Treats</span>
                <span className="profile-logo-arrow-icon" aria-hidden="true">▼</span>
              </button>

              {treatsOpen && (
                <div id="profile-logo-treats" className="profile-logo-sub">
                  <button
                    type="button"
                    className="profile-logo-item"
                    onClick={() => goFromLogoMenu("/my-treats")}
                  >
                    <span className="profile-logo-item-main">🎁 My Received Treats</span>
                    <span className="profile-logo-item-sub">Treats and greetings from your readers</span>
                  </button>
                  <button
                    type="button"
                    className="profile-logo-item"
                    onClick={() => goFromLogoMenu("/my-sent-treats")}
                  >
                    <span className="profile-logo-item-main">💝 My Sent Treats</span>
                    <span className="profile-logo-item-sub">Treats you have sent to bloggers</span>
                  </button>
                  {/* Existing link kept with the other treat options */}
                  <button
                    type="button"
                    className="profile-logo-item"
                    onClick={() => goFromLogoMenu("/treat-payouts")}
                  >
                    <span className="profile-logo-item-main">🏦 Set Up Payouts</span>
                    <span className="profile-logo-item-sub">Bank details to receive your treat earnings</span>
                  </button>
                </div>
              )}

              {/* Streak options — filled by the Streak card below */}
              <div className="profile-logo-section">
                <span className="profile-logo-section-title">🔥 Streak</span>
                <div ref={setStreakSlot} />
              </div>
            </div>
          )}
        </div>

        {/* STREAK — compact card; its buttons appear in the logo menu */}
        <Streak compact optionsTarget={logoMenuOpen ? streakSlot : null} />

        <div className="blogs-section-header">
          <span className="blogs-section-line" />
          <h2 className="blogs-section-title">✦ My Blogs</h2>
          <span className="blogs-section-line" />
        </div>


        {blogsLoading ? (
          <div className="plist-state" role="status">
            <span className="plist-spinner" aria-hidden="true" />
            <p>Loading your blogs…</p>
          </div>
        ) : blogsError ? (
          <div className="plist-state" role="alert">
            <p>{blogsError}</p>
            <button type="button" className="plist-retry-btn" onClick={retryBlogs}>
              Try again
            </button>
          </div>
        ) : userBlogs.length === 0 ? (
          <div className="profile-no-blog">
            <div className="no-blog-icon">📝</div>
            <p>No blogs yet. Start writing!</p>
          </div>
        ) : (
          <>
            {currentBlogs.map((blog, i) => {
              const isExpanded    = expandedBlogs[blog._id];
              const isCommentOpen = openCommentId === blog._id;
              const pinned         = isPinned(blog._id);
              const isLiked        = blog.likes?.some(l =>
                typeof l === "object" ? l._id === localStorage.getItem("userId") : l === localStorage.getItem("userId")
              );
              const commentsHidden = Boolean(blog.hideComments);
              const shareOff       = blog.allowShare === false;
              const saveOff        = blog.allowSave === false;

              return (
                <div
                  key={blog._id}
                  className={`profile-blog-card ${pinned ? "pinned" : ""}`}
                  style={{ animationDelay: `${i * 0.08}s` }}
                  {...smartLinks.cardProps(i + 1)}
                >
                  {/* PINNED BADGE */}
                  {pinned && (
                    <div className="pinned-badge">
                      📌 Pinned
                    </div>
                  )}

                  {/* AUTHOR ROW */}
                  <div className="profile-card-author-row">
                    <div className="profile-card-avatar-ring">
                      <Avatar src={myProfilePic} name={username} size={40} />
                    </div>
                    <div className="profile-card-author-info">
                      <span className="profile-card-author-name">{username}</span>
                      <span className="profile-card-date">
                        {new Date(blog.createdAt).toLocaleDateString("en-GB", {
                          day: "numeric", month: "short", year: "numeric",
                        })}
                      </span>
                    </div>

                    {/* ── 3-DOT MENU ── */}
                    <div
                      className="profile-card-menu-wrap"
                      ref={(el) => (menuRefs.current[blog._id] = el)}
                    >
                      <button
                        className="profile-card-menu-btn"
                        onClick={() =>
                          setOpenMenuId(prev => (prev === blog._id ? null : blog._id))
                        }
                        type="button"
                        aria-label="More options"
                      >
                        <svg viewBox="0 0 24 24" className="menu-dots-icon">
                          <circle cx="12" cy="5" r="2" />
                          <circle cx="12" cy="12" r="2" />
                          <circle cx="12" cy="19" r="2" />
                        </svg>
                      </button>

                      {openMenuId === blog._id && (
                        <div className="profile-card-menu-dropdown">
                          <button
                            className="profile-card-menu-item"
                            onClick={() => togglePin(blog._id)}
                            type="button"
                          >
                            {pinned ? "📌 Unpin" : "📌 Pin to top"}
                          </button>

                          <button
                            className="profile-card-menu-item"
                            onClick={() => { setOpenMenuId(null); navigate(`/edit-blog/${blog._id}`); }}
                            type="button"
                          >
                            ✏️ Edit blog
                          </button>

                          {/* ── BOOST YOUR BLOG ── ₹29 promotes this blog to
                              50 different readers. Available on every blog
                              listed here, since they are all your own. */}
                          <button
                            className="profile-card-menu-item"
                            onClick={() => { setOpenMenuId(null); navigate(`/boost/${blog._id}`); }}
                            type="button"
                          >
                            🚀 Boost Your Blog
                          </button>

                          <div className="profile-card-menu-divider" />

                          <button
                            className="profile-card-menu-item"
                            onClick={() => toggleBlogSetting(blog._id, "hideComments")}
                            type="button"
                          >
                            <span>💬 Comments</span>
                            <span className={`menu-item-state ${commentsHidden ? "state-off" : "state-on"}`}>
                              {commentsHidden ? "Off" : "On"}
                            </span>
                          </button>

                          <button
                            className="profile-card-menu-item"
                            onClick={() => toggleBlogSetting(blog._id, "allowShare")}
                            type="button"
                          >
                            <span>🔗 Sharing</span>
                            <span className={`menu-item-state ${shareOff ? "state-off" : "state-on"}`}>
                              {shareOff ? "Off" : "On"}
                            </span>
                          </button>

                          <button
                            className="profile-card-menu-item"
                            onClick={() => toggleBlogSetting(blog._id, "allowSave")}
                            type="button"
                          >
                            <span>🔖 Saving</span>
                            <span className={`menu-item-state ${saveOff ? "state-off" : "state-on"}`}>
                              {saveOff ? "Off" : "On"}
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* TITLE */}
                  <h3 className="profile-blog-title">{blog.title}</h3>

                  {/* CONTENT */}
                  <p className="profile-blog-content">
                    {isExpanded
                      ? blog.content || ""
                      : blog.content?.length > 150
                        ? blog.content.slice(0, 150) + "..."
                        : blog.content || ""}
                    {blog.content?.length > 150 && (
                      <span
                        className="read-more-btn"
                        onClick={() => toggleReadMore(blog._id)}
                      >
                        {isExpanded ? " Read Less" : " Read More"}
                      </span>
                    )}
                  </p>

                  {/* IMAGE */}
                  {blog.image && (
                    <img
                      src={blog.image}
                      className="profile-blog-image"
                      alt="blog"
                    />
                  )}

                  {/* VIDEO */}
                  {blog.video && (
                    <div className="profile-video-wrap">
                      <video
                        controls
                        className="profile-blog-video"
                        ref={(el) => (videoRefs.current[blog._id] = el)}
                        onPlay={() => {
                          Object.keys(videoRefs.current).forEach((id) => {
                            if (id !== blog._id && videoRefs.current[id]) {
                              videoRefs.current[id].pause();
                            }
                          });
                        }}
                      >
                        <source src={blog.video} />
                      </video>
                    </div>
                  )}

                  {/* ── ACTIONS — same style as ViewBlogs ── */}
                  <div className="blog-actions">
                    <div className="actions-left">

                      {/* LIKE */}
                      <LikeButton
                        blogId={blog._id}
                        initialLikes={blog.likes?.length || 0}
                        initialLiked={isLiked}
                        token={token}
                      />

                      {/* COMMENT */}
                      {!commentsHidden && (
                        <button
                          className={`action-btn comment-btn ${isCommentOpen ? "active" : ""}`}
                          onClick={() => toggleComments(blog._id)}
                          type="button"
                        >
                          <svg viewBox="0 0 24 24" className="action-icon comment-icon">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                          </svg>
                          <span>{commentCounts[blog._id] ?? 0}</span>
                        </button>
                      )}

                      {/* SHARE */}
                      {!shareOff && (
                        <div className="share-wrapper">
                          <button
                            className="action-btn share-btn"
                            onClick={() => handleShare(blog)}
                            type="button"
                          >
                            <svg viewBox="0 0 24 24" className="action-icon share-icon">
                              <line x1="22" y1="2" x2="11" y2="13" />
                              <polygon points="22 2 15 22 11 13 2 9 22 2" />
                            </svg>
                            <span>{shareCounts[blog._id] ?? 0}</span>
                          </button>
                          {shareToast === blog._id && (
                            <span className="share-toast">Link copied!</span>
                          )}
                        </div>
                      )}

                    </div>

                    <div className="actions-right">

                      {/* SAVE COUNT — read-only. This page lists the author's
                          own blogs, so there is no save button to attach it to.
                          Shown even when saving is off, since it's a count, not a toggle. */}
                      <span className="blog-save-count" title="Saves">
                        <svg viewBox="0 0 24 24" className="save-count-icon" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                        {saveCounts[blog._id] ?? 0}
                      </span>

                      {/* VIEW COUNT */}
                      <span className="blog-view-count">
                        <svg viewBox="0 0 24 24" className="action-icon view-icon" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                        {viewCounts[blog._id] ?? blog.viewCount ?? 0}
                      </span>

                      {/* DELETE */}
                      <button
                        className="action-btn profile-delete-action-btn"
                        onClick={() => deleteBlog(blog._id)}
                        type="button"
                        title="Delete blog"
                      >
                        <svg viewBox="0 0 24 24" className="action-icon delete-icon" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14H6L5 6" />
                          <path d="M10 11v6" />
                          <path d="M14 11v6" />
                          <path d="M9 6V4h6v2" />
                        </svg>
                      </button>

                    </div>
                  </div>

                  {/* COMMENTS */}
                  {!commentsHidden && (
                    <Comments
                      blogId={blog._id}
                      blogAuthor={username}
                      currentUsername={username}
                      currentUserPic={myProfilePic}
                      isOpen={isCommentOpen}
                    />
                  )}

                  <p className="blog-time">
                    🕒 {new Date(blog.createdAt).toLocaleString()}
                  </p>
                </div>
              );
            })}

            {/* Progressive loading — replaces the old per-page number buttons */}
            <ListLoader list={blogList} total={sortedBlogs.length} />
          </>
        )}
      </div>
    </div>
  );
}

export default Profile;